import React, { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, IconButton } from '@material-ui/core'
import { Icon } from '@iconify/react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CoreButton } from 'src/components/core'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import { fetchCatalog, saveCatalogOrder } from '../index'

// One page big enough to hold a shop's catalog; the ordering list is not paginated.
const ORDERING_PAGE_SIZE = 200

const findCatalogElement = editor => {
  const root = editor.model.document.getRoot()
  if (!root) return null

  const range = editor.model.createRangeIn(root)
  for (const value of range.getWalker()) {
    const item = value.item
    if (
      item.is &&
      item.is('element', 'dynamicList') &&
      item.getAttribute('type') === 'catalog'
    ) {
      return item
    }
  }

  return null
}

const SortableRow = ({ item, index, total, onMove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key })

  const isCollection = item.type === 1 || item.type === 'Collection'

  return (
    <Box
      ref={setNodeRef}
      style={{
        // Translate, not Transform: rows have different heights, and Transform would scale the
        // dragged row to the size of the one it is passing over.
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : 'auto',
        position: 'relative',
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        marginBottom: '8px',
        borderRadius: '10px',
        border: '1px solid #e6e9ef',
        backgroundColor: isDragging ? '#f4f8fd' : '#fff',
        boxShadow: isDragging ? '0 8px 20px rgba(15, 23, 42, 0.12)' : 'none',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ display: 'flex', cursor: 'grab', color: '#9aa3af' }}
      >
        <Icon icon="mdi:drag" width={18} height={18} />
      </Box>

      <Typography
        sx={{
          fontSize: '14px',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={item.name || 'Untitled'}
      >
        {item.name || 'Untitled'}
      </Typography>

      <Typography
        component="span"
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: '#6b7280',
          backgroundColor: '#f3f5f9',
          borderRadius: '6px',
          padding: '4px 8px',
        }}
      >
        {isCollection ? 'LEARNING PATH' : 'COURSE'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <IconButton
          size="small"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          sx={{ p: '2px' }}
        >
          <Icon icon="mdi:chevron-up" width={18} height={18} />
        </IconButton>
        <IconButton
          size="small"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          sx={{ p: '2px' }}
        >
          <Icon icon="mdi:chevron-down" width={18} height={18} />
        </IconButton>
      </Box>
    </Box>
  )
}

/**
 * Drag & drop ordering for the Catalog block. The saved order lives in the CatalogOrderItems
 * table keyed by the block's catalog id, so anything published later has no row there and
 * falls to the end of the catalog.
 */
const CatalogOrderingDialog = ({ editor, dispatch }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [catalogId, setCatalogId] = useState('')

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const catalogElement = findCatalogElement(editor)
      const id = catalogElement?.getAttribute('catalogId') || ''
      if (!cancelled) setCatalogId(id)

      try {
        const result = await fetchCatalog({
          catalogId: id,
          pageSize: ORDERING_PAGE_SIZE,
        })
        if (!cancelled) setItems(result.items)
      } catch (error) {
        console.error('Failed to load catalog ordering:', error)
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [editor])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  const move = (from, to) => {
    setItems(current => {
      if (to < 0 || to >= current.length) return current
      return arrayMove(current, from, to)
    })
  }

  const handleDragEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setItems(current => {
      const oldIndex = current.findIndex(item => item.key === active.id)
      const newIndex = current.findIndex(item => item.key === over.id)
      if (oldIndex === -1 || newIndex === -1) return current
      return arrayMove(current, oldIndex, newIndex)
    })
  }

  const refreshPreview = () => {
    const catalogElement = findCatalogElement(editor)
    if (!catalogElement) return

    editor.editing.reconvertItem(catalogElement)
    editor.editing.view.forceRender()
  }

  const persist = async nextItems => {
    if (!catalogId) return
    setSaving(true)
    try {
      await saveCatalogOrder(catalogId, nextItems)
      refreshPreview()
      dispatch(messageBoxHideAction())
    } catch (error) {
      console.error('Failed to save catalog ordering:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
        Drag the items to change the order they appear in the catalog.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Typography
          sx={{
            fontSize: '14px',
            color: '#6b7280',
            py: '24px',
            textAlign: 'center',
          }}
        >
          Nothing to show here yet
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: '4px' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map(item => item.key)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item, index) => (
                <SortableRow
                  key={item.key}
                  item={item}
                  index={index}
                  total={items.length}
                  onMove={move}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <CoreButton
          onClick={() => persist([])}
          variant="text"
          color="secondary"
          disabled={loading || saving}
        >
          Reset to publish date
        </CoreButton>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <CoreButton
            onClick={() => dispatch(messageBoxHideAction())}
            variant="text"
            color="secondary"
          >
            Cancel
          </CoreButton>
          <CoreButton
            onClick={() => persist(items)}
            color="primary"
            disabled={loading || saving || !catalogId}
          >
            Save
          </CoreButton>
        </Box>
      </Box>
    </Box>
  )
}

export const handleCatalogOrderingModal = (dispatch, editor) => {
  dispatch(messageBoxHideAction())

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        buttons: [],
        title: 'Edit order',
        draggable: false,
        maxWidth: '560px',
        component: (
          <CatalogOrderingDialog editor={editor} dispatch={dispatch} />
        ),
      }),
    ),
  )
}

export default CatalogOrderingDialog
